import {
    CorrespondenceCreateDto,
    CorrespondenceDetailsDto,
    CorrespondenceListElementDto,
    CorrespondenceListElementMinDto,
    CorrespondenceUpdateDto,
} from 'types/correspondence';
import { APINotificationsSlice } from './notifications';
import { portalAPI } from './portalAPI';
import { accumulatedQueryWithFilters } from './util/endpointBuilder';

export const APICorrespondenceSlice = portalAPI
    .enhanceEndpoints({ addTagTypes: ['Correspondence', 'CorrespondenceMinList', 'CorrespondenceList'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getCorrespondenceById: builder.query<CorrespondenceDetailsDto, string>({
                query: (entityId) => `/v1/correspondence/${entityId}`,
                providesTags: (_1, _2, arg) => [{ type: 'Correspondence', id: arg }],
                keepUnusedDataFor: 0,
            }),
            getCorrespondenceMinList: accumulatedQueryWithFilters<CorrespondenceListElementMinDto>(
                builder,
                '/v1/correspondence/min',
                {
                    providesTags: ['CorrespondenceMinList'],
                }
            ),
            getCorrespondenceListWithFilters: accumulatedQueryWithFilters<CorrespondenceListElementDto>(
                builder,
                '/v1/correspondence',
                {
                    providesTags: ['CorrespondenceList'],
                }
            ),
            addCorrespondence: builder.mutation<CorrespondenceDetailsDto, CorrespondenceCreateDto>({
                query: ({ ...body }) => ({
                    url: '/v1/correspondence',
                    method: 'POST',
                    body,
                }),
                invalidatesTags: ['CorrespondenceList', 'CorrespondenceMinList'],
                onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                    await queryFulfilled;
                    dispatch(APINotificationsSlice.util.invalidateTags(['Notifications']));
                },
            }),
            updateCorrespondence: builder.mutation<CorrespondenceDetailsDto, CorrespondenceUpdateDto>({
                query: ({ id, ...body }) => ({
                    url: `/v1/correspondence/${id}`,
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: (_1, _2, arg) =>
                    arg.id
                        ? [{ type: 'Correspondence', id: arg.id }, 'CorrespondenceList', 'CorrespondenceMinList']
                        : ['CorrespondenceList', 'CorrespondenceMinList'],
                onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
                    await queryFulfilled;
                    dispatch(APINotificationsSlice.util.invalidateTags(['Notifications']));
                },
            }),
            deleteCorrespondence: builder.mutation<boolean, { id: string }>({
                query: ({ id }) => ({
                    url: `/v1/correspondence/${id}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['CorrespondenceList', 'CorrespondenceMinList'],
            }),
            completeCorrespondences: builder.mutation<void, { correspondenceIds: number[]; outgoingDate: string }>({
                query: (body) => ({
                    url: '/v1/correspondence/complete',
                    method: 'PUT',
                    body,
                }),
                invalidatesTags: ['CorrespondenceList', 'CorrespondenceMinList'],
            }),
        }),
    });

export const {
    useGetCorrespondenceByIdQuery,
    useLazyGetCorrespondenceMinListQuery,
    useLazyGetCorrespondenceListWithFiltersQuery,
    useAddCorrespondenceMutation,
    useUpdateCorrespondenceMutation,
    useDeleteCorrespondenceMutation,
    useCompleteCorrespondencesMutation,
} = APICorrespondenceSlice;
